/**
 * ProfileBadges — badges strip on a member's public profile page. Loads
 * every course completion badge for the given user and renders them as
 * tier-coloured pills. Silently hides if no badges.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Award } from 'lucide-react';

const TIER_STYLES = {
  free:   'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
  tier_1: 'bg-amber-500/10 text-amber-800 dark:text-amber-400 border-amber-500/40',
  tier_2: 'bg-primary/10 text-primary border-primary/40',
  tier_3: 'bg-secondary/10 text-secondary border-secondary/40',
};

const ProfileBadges = ({ userId }) => {
  const { API } = useContext(AuthContext);
  const [badges, setBadges] = useState([]);
  const [tier, setTier] = useState(null); // { key, label, min } | null
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    Promise.all([
      axios.get(`${API}/lms/badges/${userId}`).then((r) => r.data?.badges || []).catch(() => []),
      axios.get(`${API}/users/${userId}/referral-tier`).then((r) => r.data?.tier || null).catch(() => null),
    ]).then(([bs, t]) => {
      if (!alive) return;
      setBadges(bs);
      setTier(t);
    }).finally(() => alive && setLoaded(true));
    return () => { alive = false; };
  }, [API, userId]);

  if (!loaded || (badges.length === 0 && !tier)) return null;

  const TIER_STYLE = {
    sprout: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/40',
    rising: 'bg-amber-500/10 text-amber-800 dark:text-amber-400 border-amber-500/40',
    elite:  'bg-primary/10 text-primary border-primary/50',
  };

  return (
    <div className="pro-card p-5" data-testid="profile-badges">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          Badges earned · {badges.length + (tier ? 1 : 0)}
        </div>
        <Award className="w-4 h-4 text-primary" />
      </div>
      <div className="flex flex-wrap gap-2">
        {tier && (
          <div
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${TIER_STYLE[tier.key] || TIER_STYLE.sprout}`}
            data-testid={`profile-badge-referral-${tier.key}`}
            title={`Earned by referring ${tier.min}+ signups`}
          >
            <Award className="w-3.5 h-3.5" />
            <span className="text-xs font-bold">{tier.label}</span>
            <span className="text-[9px] uppercase tracking-widest opacity-70">Referrals</span>
          </div>
        )}
        {badges.map((b) => (
          <div
            key={b.id}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${TIER_STYLES[b.course_tier] || TIER_STYLES.free}`}
            data-testid={`profile-badge-${b.course_id}`}
            title={`Earned ${new Date(b.earned_at).toLocaleDateString()}`}
          >
            <Award className="w-3.5 h-3.5" />
            <span className="text-xs font-bold">{b.course_title || 'Course badge'}</span>
            <span className="text-[9px] uppercase tracking-widest opacity-70">
              {new Date(b.earned_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileBadges;
